/* eslint-disable @typescript-eslint/no-explicit-any */
// Backups section of the admin console (ADR 0017, docs/adr/0017-backups.md "Admin console"): status, "back up now",
// list, download (password re-entry) and restore (password + typed `RESTORE <created_at>`), with job polling every
// 2 s. Restore puts the whole platform into maintenance: other admin calls answer 503 `maintenance`, the job
// endpoint is exempt, so polling tolerates 503/network failures and keeps going.
// The admin console is Persian (RTL) like its reference; every string of this section lives in BK_STR with an
// English twin so the section can follow a console language switch when one exists.
import type { AdminController } from './controller';
import { AdminError, type Backup, type BackupJob, type BackupsOverview } from './ports';
import { AMBER, GREEN, MUTED, RED, soft } from './constants';
import { ago, dateTime } from './fmt';

export const POLL_MS = 2000;
/** Consecutive failed job reads tolerated (~30 min) before the console says it lost track of the job. */
export const POLL_GIVE_UP_MS = 30 * 60 * 1000;
/** The agent reports in continuously; older than this = the backup service is not running. */
export const AGENT_STALE_MS = 2 * 60 * 1000;

export type Lang = 'fa' | 'en';

// ── strings ──
const FA = {
  title: 'پشتیبان‌گیری', intro: 'از همه اطلاعات تسکول (پایگاه داده و فایل‌ها) هر ساعت نسخه پشتیبان رمزنگاری‌شده گرفته می‌شود.',
  backupNow: 'پشتیبان‌گیری الان', starting: 'در حال درخواست…',
  status: 'وضعیت', lastSuccess: 'آخرین پشتیبان موفق', never: 'هنوز پشتیبانی گرفته نشده', lastFailure: 'آخرین تلاش ناموفق',
  next: 'پشتیبان بعدی', agent: 'سرویس پشتیبان‌گیری', agentOk: (a: string) => 'فعال — ' + a, agentNone: 'گزارشی نرسیده',
  encryption: 'رمزنگاری', on: 'روشن', off: 'خاموش', offsite: 'نسخه بیرون از سرور', configured: 'پیکربندی شده', notConfigured: 'پیکربندی نشده',
  schTitle: 'زمان‌بندی پشتیبان‌گیری', schEvery: 'هر', schAt: 'شروع از ساعت', schTz: 'به وقت تهران', schSave: 'ذخیره', schSaving: 'در حال ذخیره…', schSaved: 'زمان‌بندی ذخیره شد',
  schHint: (e: number, at: string) => e === 24 ? 'هر روز ساعت ' + at + ' یک نسخه گرفته می‌شود.' : 'از ساعت ' + at + '، هر ' + e + ' ساعت یک نسخه گرفته می‌شود.',
  schOpt: (e: number) => e === 24 ? 'روز یک بار' : e + ' ساعت', schText: (e: number, at: string) => e === 24 ? 'روزانه ' + at : 'هر ' + e + ' ساعت از ' + at,
  keep: 'نگهداری', keepVal: (h: string, d: string, m: string, l: string) => h + ' ساعتی · ' + d + ' روزانه · ' + m + ' ماهانه · ' + l + ' روی سرور',
  stale: 'آخرین پشتیبان موفق قدیمی است؛ پشتیبان‌گیری خودکار درست کار نمی‌کند.',
  agentDown: 'سرویس پشتیبان‌گیری اجرا نمی‌شود؛ پشتیبان‌گیری و بازگردانی تا راه‌اندازی دوباره آن انجام نمی‌شوند.',
  encOff: 'کلید رمزنگاری تنظیم نشده است؛ تا تنظیم آن روی سرور، پشتیبان‌گیری غیرفعال است.',
  offsiteOff: 'فضای ذخیره‌سازی بیرونی تنظیم نشده است؛ نسخه‌ها فقط روی همین سرور می‌مانند.',
  colTime: 'زمان', colKind: 'نوع', colStatus: 'وضعیت', colSize: 'حجم', colCounts: 'محتوا', colSchema: 'نسخه طرح', colWhere: 'محل نگهداری',
  kind: { scheduled: 'زمان‌بندی‌شده', manual: 'دستی', pre_restore: 'پیش از بازگردانی' } as Record<string, string>,
  bstatus: { running: 'در حال ساخت', ok: 'سالم', failed: 'ناموفق' } as Record<string, string>,
  local: 'همین سرور', offsiteState: { uploaded: 'بیرونی: ارسال شد', pending: 'بیرونی: در انتظار', failed: 'بیرونی: ناموفق', disabled: 'بیرونی: غیرفعال' } as Record<string, string>,
  counts: (u: string, t: string, f: string) => u + ' کاربر · ' + t + ' تسک · ' + f + ' فایل',
  download: 'دانلود', restore: 'بازگردانی',
  empty: 'هنوز نسخه پشتیبانی وجود ندارد.', loading: 'در حال بارگذاری…', loadError: 'فهرست نسخه‌ها بارگذاری نشد.', retry: 'دوباره امتحان کنید',
  denied: 'نقش شما به بخش پشتیبان‌گیری دسترسی ندارد.', unavailable: 'پشتیبان‌گیری در این محیط در دسترس نیست.',
  jobBackup: 'پشتیبان‌گیری دستی', jobRestore: 'بازگردانی نسخه پشتیبان',
  jstatus: { queued: 'در صف', running: 'در حال اجرا', done: 'انجام شد', failed: 'ناموفق' } as Record<string, string>,
  steps: {
    queued: 'در انتظار سرویس پشتیبان‌گیری', backup: 'ساختن نسخه پشتیبان (پایگاه داده، فایل‌ها، رمزنگاری)', maintenance: 'روشن کردن حالت تعمیر',
    safety_backup: 'گرفتن نسخه ایمنی از وضعیت فعلی', download: 'آماده کردن و بررسی نسخه انتخاب‌شده', database: 'بازگرداندن پایگاه داده',
    files: 'بازگرداندن فایل‌ها', migrations: 'به‌روزرسانی ساختار پایگاه داده', rollback: 'برگرداندن وضعیت پیش از بازگردانی', done: 'پایان کار',
  } as Record<string, string>,
  backupDone: 'نسخه پشتیبان ساخته شد', backupFailed: 'پشتیبان‌گیری ناموفق بود', jobLost: 'وضعیت کار از سرور خوانده نشد',
  waiting: 'سرور در دسترس نیست؛ دوباره بررسی می‌شود…',
  // download dialog
  dlTitle: 'دانلود نسخه پشتیبان', dlText: 'فایل رمزگشایی‌شده (tar.gz) شامل همه اطلاعات تسکول است؛ آن را در جای امن نگه دارید. برای ادامه رمز عبور فعلی خود را وارد کنید.',
  password: 'رمز عبور فعلی', cancel: 'انصراف', dlGo: 'دانلود', dlStarted: 'دانلود شروع شد',
  // restore dialog
  rsTitle: 'بازگردانی نسخه پشتیبان',
  rsPoints: (when: string) => [
    'اطلاعات همه کاربران به وضعیت ' + when + ' برمی‌گردد.',
    'هر چیزی که بعد از این زمان ثبت شده از بین می‌رود. پیش از شروع، یک نسخه ایمنی از وضعیت فعلی گرفته می‌شود.',
    'در تمام مدت بازگردانی سایت در حالت تعمیر است و هیچ‌کس نمی‌تواند از آن استفاده کند.',
  ],
  rsPhraseLabel: 'برای تأیید، این عبارت را دقیقاً بنویسید:', copy: 'کپی', copied: 'عبارت کپی شد', rsPhrasePh: 'عبارت تأیید',
  rsGo: 'بازگردانی', rsMismatch: 'عبارت با متن بالا یکی نیست',
  // banner
  bnRestoring: 'در حال بازگردانی… سایت در حالت تعمیر است.', bnDone: 'بازگردانی انجام شد. برای دیدن اطلاعات بازگردانده‌شده صفحه را دوباره بارگذاری کنید.',
  bnFailed: 'بازگردانی ناموفق بود.', reload: 'بارگذاری دوباره', close: 'بستن',
  errors: {
    invalid_password: 'رمز عبور درست نیست', rate_limited: 'تلاش‌ها زیاد بود؛ کمی بعد دوباره امتحان کنید', confirmation_mismatch: 'عبارت تأیید درست نیست',
    restore_in_progress: 'یک بازگردانی دیگر در جریان است', backup_not_restorable: 'این نسخه قابل بازگردانی نیست', backup_not_found: 'این نسخه دیگر وجود ندارد',
    maintenance: 'سایت در حالت تعمیر است؛ کمی بعد دوباره امتحان کنید', forbidden: 'نقش شما اجازه این کار را ندارد', network: 'اتصال به سرور برقرار نیست؛ دوباره امتحان کنید',
    generic: 'خطایی پیش آمد؛ دوباره امتحان کنید',
  } as Record<string, string>,
  ago, inFuture: (n: string, unit: 'm' | 'h' | 'd') => 'تا ' + n + ' ' + ({ m: 'دقیقه', h: 'ساعت', d: 'روز' })[unit] + ' دیگر', soon: 'تا چند لحظه دیگر',
  unit: ['بایت', 'کیلوبایت', 'مگابایت', 'گیگابایت', 'ترابایت'], numLocale: 'fa-IR',
};
type Strings = typeof FA;
const EN: Strings = {
  title: 'Backups', intro: 'All TaskCool data (database and files) is backed up hourly, encrypted.',
  backupNow: 'Back up now', starting: 'Requesting…',
  status: 'Status', lastSuccess: 'Last successful backup', never: 'No backup yet', lastFailure: 'Last failed attempt',
  next: 'Next backup', agent: 'Backup service', agentOk: a => 'Running — ' + a, agentNone: 'No report yet',
  encryption: 'Encryption', on: 'On', off: 'Off', offsite: 'Offsite copy', configured: 'Configured', notConfigured: 'Not configured',
  schTitle: 'Backup schedule', schEvery: 'Every', schAt: 'Starting at', schTz: 'Tehran time', schSave: 'Save', schSaving: 'Saving…', schSaved: 'Schedule saved',
  schHint: (e: number, at: string) => e === 24 ? 'A backup is taken every day at ' + at + '.' : 'From ' + at + ', a backup is taken every ' + e + ' hours.',
  schOpt: (e: number) => e === 24 ? 'Once a day' : e + (e === 1 ? ' hour' : ' hours'), schText: (e: number, at: string) => e === 24 ? 'daily ' + at : 'every ' + e + ' h from ' + at,
  keep: 'Retention', keepVal: (h, d, m, l) => h + ' hourly · ' + d + ' daily · ' + m + ' monthly · ' + l + ' on this server',
  stale: 'The last successful backup is too old; scheduled backups are not working.',
  agentDown: 'The backup service is not running; backups and restores wait until it is back.',
  encOff: 'No encryption key is configured; backups are disabled until one is set on the server.',
  offsiteOff: 'No offsite storage is configured; copies stay on this server only.',
  colTime: 'Time', colKind: 'Kind', colStatus: 'Status', colSize: 'Size', colCounts: 'Contents', colSchema: 'Schema', colWhere: 'Stored',
  kind: { scheduled: 'Scheduled', manual: 'Manual', pre_restore: 'Before restore' },
  bstatus: { running: 'Running', ok: 'OK', failed: 'Failed' },
  local: 'This server', offsiteState: { uploaded: 'Offsite: uploaded', pending: 'Offsite: pending', failed: 'Offsite: failed', disabled: 'Offsite: disabled' },
  counts: (u, t, f) => u + ' users · ' + t + ' tasks · ' + f + ' files',
  download: 'Download', restore: 'Restore',
  empty: 'No backups yet.', loading: 'Loading…', loadError: 'Backups could not be loaded.', retry: 'Try again',
  denied: 'Your role has no access to backups.', unavailable: 'Backups are not available here.',
  jobBackup: 'Manual backup', jobRestore: 'Restore',
  jstatus: { queued: 'Queued', running: 'Running', done: 'Done', failed: 'Failed' },
  steps: {
    queued: 'Waiting for the backup service', backup: 'Creating the backup (database, files, encryption)', maintenance: 'Turning on maintenance',
    safety_backup: 'Safety backup of the current state', download: 'Preparing and verifying the chosen backup', database: 'Restoring the database',
    files: 'Restoring files', migrations: 'Updating the database schema', rollback: 'Rolling back to the state before the restore', done: 'Finishing',
  },
  backupDone: 'Backup created', backupFailed: 'Backup failed', jobLost: 'Could not read the job status',
  waiting: 'Server unreachable; checking again…',
  dlTitle: 'Download backup', dlText: 'The decrypted file (tar.gz) contains all TaskCool data; keep it somewhere safe. Enter your current password to continue.',
  password: 'Current password', cancel: 'Cancel', dlGo: 'Download', dlStarted: 'Download started',
  rsTitle: 'Restore backup',
  rsPoints: when => [
    'Everyone’s data goes back to ' + when + '.',
    'Everything recorded after that time is lost. A safety backup of the current state is taken first.',
    'The site is in maintenance for the whole restore; nobody can use it.',
  ],
  rsPhraseLabel: 'To confirm, type this phrase exactly:', copy: 'Copy', copied: 'Phrase copied', rsPhrasePh: 'Confirmation phrase',
  rsGo: 'Restore', rsMismatch: 'The phrase does not match',
  bnRestoring: 'Restoring… the site is in maintenance.', bnDone: 'Restore finished. Reload to see the restored data.',
  bnFailed: 'Restore failed.', reload: 'Reload', close: 'Close',
  errors: {
    invalid_password: 'Wrong password', rate_limited: 'Too many attempts; try again shortly', confirmation_mismatch: 'The confirmation phrase is wrong',
    restore_in_progress: 'Another restore is running', backup_not_restorable: 'This backup cannot be restored', backup_not_found: 'This backup no longer exists',
    maintenance: 'The site is in maintenance; try again shortly', forbidden: 'Your role is not allowed to do this', network: 'Cannot reach the server; try again',
    generic: 'Something went wrong; try again',
  },
  ago: (iso, now) => {
    const t = iso ? Date.parse(iso) : NaN; if (!Number.isFinite(t)) return '—';
    const m = Math.floor(((now ?? Date.now()) - t) / 60000);
    return m < 1 ? 'just now' : m < 60 ? m + ' min ago' : m < 1440 ? Math.floor(m / 60) + ' h ago' : Math.floor(m / 1440) + ' days ago';
  },
  inFuture: (n, unit) => 'in ' + n + ' ' + ({ m: 'min', h: 'h', d: 'days' })[unit], soon: 'in a moment',
  unit: ['B', 'KB', 'MB', 'GB', 'TB'], numLocale: 'en-US',
};
export const BK_STR: Record<Lang, Strings> = { fa: FA, en: EN };

// ── pure helpers ──
const num = (n: number, L: Strings, digits = 0): string => n.toLocaleString(L.numLocale, { maximumFractionDigits: digits });

/** Human size with binary units: one decimal below 10 units ("۱٫۵ مگابایت", "1.5 MB"), whole numbers above. */
export function backupSize(bytes: number | null | undefined, lang: Lang = 'fa'): string {
  const L = BK_STR[lang];
  if (bytes === null || bytes === undefined || !Number.isFinite(bytes) || bytes < 0) return '—';
  let v = bytes, i = 0;
  while (v >= 1024 && i < L.unit.length - 1) { v /= 1024; i++; }
  return num(i === 0 ? v : v < 10 ? Math.round(v * 10) / 10 : Math.round(v), L, i === 0 ? 0 : 1) + ' ' + L.unit[i];
}

/** The exact confirmation the server expects (ADR 0017): `RESTORE <created_at to the second, UTC>`; the server also
 *  sends it as `restore_confirmation`, which wins when present. */
export const confirmPhrase = (createdAt: string, given?: string | null): string =>
  given || 'RESTORE ' + new Date(createdAt).toISOString().replace(/\.\d{3}Z$/, 'Z');
/** Typed phrase normalised for comparison: surrounding spaces trimmed, Persian/Arabic digits → ASCII. */
export const normalizePhrase = (typed: string): string =>
  typed.trim().replace(/[۰-۹]/g, d => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(d))).replace(/[٠-٩]/g, d => String('٠١٢٣٤٥٦٧٨٩'.indexOf(d)));
/** Restore button gate: a password and the exact phrase (`expected` = confirmPhrase(…)). */
export const restoreReady = (password: string, typed: string, expected: string): boolean =>
  password.length > 0 && normalizePhrase(typed) === expected;

export const jobActive = (j: BackupJob | null | undefined): boolean => !!j && (j.status === 'queued' || j.status === 'running');

/** Future instant in words ("تا ۲۳ دقیقه دیگر"); a past one reads as "… ago". */
export function until(iso: string | null | undefined, lang: Lang = 'fa', now = Date.now()): string {
  const L = BK_STR[lang];
  const t = iso ? Date.parse(iso) : NaN;
  if (!Number.isFinite(t)) return '—';
  if (t < now) return L.ago(iso, now);
  const m = Math.ceil((t - now) / 60000);
  if (m <= 1) return L.soon;
  if (m < 60) return L.inFuture(num(m, L), 'm');
  if (m < 1440) return L.inFuture(num(Math.round(m / 60), L), 'h');
  return L.inFuture(num(Math.round(m / 1440), L), 'd');
}

export function stepLabel(step: string | null | undefined, status: string, lang: Lang = 'fa'): string {
  const L = BK_STR[lang];
  if (step && L.steps[step]) return L.steps[step]!;
  if (step) return step.replace(/[_-]+/g, ' ');
  return status === 'queued' ? L.steps.queued! : '';
}

export function errorText(e: unknown, lang: Lang = 'fa'): string {
  const L = BK_STR[lang].errors;
  if (e instanceof AdminError) {
    if (e.code && L[e.code]) return L[e.code]!;
    if (e.kind === 'forbidden') return L.forbidden!;
    if (e.kind === 'network') return L.network!;
    if (e.kind === 'rejected' && e.message) return e.message;
  }
  return L.generic!;
}

// ── state + controller ──
export interface BkDialog { id: string; createdAt: string; expected?: string; password: string; phrase: string; error: string; busy: boolean }
const expectedOf = (d: BkDialog): string => d.expected || confirmPhrase(d.createdAt);
export interface BkState {
  data: BackupsOverview | null; loading: boolean; error: string | null; denied: boolean; unavailable: boolean;
  job: BackupJob | null; jobError: string | null; offline: boolean; requesting: boolean;
  dl: BkDialog | null; rs: BkDialog | null;
  banner: null | 'restoring' | 'restored' | 'restore_failed'; bannerError: string | null;
  /** schedule form (null until the overview loaded) */
  sch: { every: number; at: string; busy: boolean; error: string } | null;
}
export const bkInitial = (): BkState => ({
  data: null, loading: false, error: null, denied: false, unavailable: false, job: null, jobError: null, offline: false, requesting: false,
  dl: null, rs: null, banner: null, bannerError: null, sch: null,
});

/**
 * Job state machine: queued → running → done | failed. While active the job is re-read every POLL_MS. Any failed
 * read (503 `maintenance`, 401/5xx/network while a restore reloads tables) keeps the polling going for up to
 * POLL_GIVE_UP_MS; only a job answer with done/failed ends it. A manual backup's 401 goes to the sign-in gate.
 */
export class BackupsCtl {
  lang: Lang = 'fa';
  private timer: ReturnType<typeof setTimeout> | undefined;
  private disposed = false;
  private misses = 0;
  constructor(private c: AdminController) {}

  get st(): BkState { return this.c.state.bk as BkState; }
  get L(): Strings { return BK_STR[this.lang]; }
  allowed(): boolean { return this.c.can('manageBackups'); }

  /** Reads GET /backups; true when it succeeded. */
  setEvery(v: number): void { if (this.st.sch) this.st.sch = { ...this.st.sch, every: v, error: '' }; this.c.setState({}); }
  setAt(v: string): void { if (this.st.sch) this.st.sch = { ...this.st.sch, at: v, error: '' }; this.c.setState({}); }
  async saveSchedule(): Promise<void> {
    const st = this.st, port = this.c.port;
    if (!st.sch || st.sch.busy || !port.backupSchedule) return;
    st.sch = { ...st.sch, busy: true, error: '' }; this.c.setState({});
    try {
      const r = await port.backupSchedule(st.sch.every, st.sch.at);
      st.sch = { every: r.schedule.every_hours, at: r.schedule.at, busy: false, error: '' };
      this.c.toast(this.L.schSaved);
      await this.load();
    } catch (e) { st.sch = { ...st.sch!, busy: false, error: errorText(e, this.lang) }; this.c.setState({}); }
  }
  async load(): Promise<boolean> {
    const st = this.st, port = this.c.port;
    if (!this.allowed()) { st.denied = true; return false; }
    if (!port.backups) { st.unavailable = true; return false; }
    st.loading = true; st.error = null;
    try {
      const data = await port.backups();
      st.data = { ...data, backups: [...(data.backups || [])].sort((a, b) => Date.parse(b.created_at) - Date.parse(a.created_at)), jobs: data.jobs || [] };
      const sc = typeof data.config?.schedule === 'object' ? data.config.schedule : null;
      if (sc && !(st.sch && st.sch.busy)) st.sch = { every: sc.every_hours, at: sc.at, busy: false, error: '' };
      st.denied = false;
      // Re-attach to a job that is still running (page reloaded mid-backup / mid-restore).
      if (!jobActive(st.job)) { const active = st.data.jobs.find(jobActive); if (active) this.track(active); }
      return true;
    } catch (e) {
      if (e instanceof AdminError && e.kind === 'auth') { this.c.fail(e); return false; }
      if (e instanceof AdminError && (e.kind === 'forbidden' || e.code === 'not_found')) st.denied = true;
      else st.error = errorText(e, this.lang);
      return false;
    } finally { st.loading = false; }
  }

  async backupNow(): Promise<void> {
    const st = this.st, port = this.c.port;
    if (!port.backupNow || st.requesting || jobActive(st.job)) return;
    st.requesting = true;
    try { const r = await port.backupNow(); this.track(r.job); }
    catch (e) { if (e instanceof AdminError && e.kind === 'auth') this.c.fail(e); else this.c.toast(errorText(e, this.lang)); }
    finally { st.requesting = false; }
  }

  /** Shows a job and keeps polling it while queued/running; a transition into done/failed finishes it once. */
  track(job: BackupJob): void {
    const st = this.st, prev = st.job;
    clearTimeout(this.timer);
    if (prev?.id !== job.id) this.misses = 0;
    st.job = job; st.jobError = null;
    if (job.type === 'restore' && jobActive(job)) { st.banner = 'restoring'; st.bannerError = null; }
    if (jobActive(job)) { this.timer = setTimeout(() => void this.poll(job.id), POLL_MS); return; }
    if (!prev || prev.id !== job.id || jobActive(prev)) this.finish(job);
  }

  async poll(id: string): Promise<void> {
    const st = this.st, port = this.c.port;
    if (this.disposed || !port.backupJob || st.job?.id !== id) return;
    try {
      const r = await port.backupJob(id);
      if (this.disposed || st.job?.id !== id) return;
      st.offline = false; this.misses = 0;
      this.track(r.job);
    } catch (e) {
      if (this.disposed || st.job?.id !== id) return;
      // During a restore every endpoint but this one answers 503 `maintenance`, and even this one may briefly
      // fail (401/5xx/network) while tables are reloaded: keep polling until a job answer says done/failed.
      // A manual backup hands a lost session to the sign-in gate.
      if (st.job.type === 'backup' && e instanceof AdminError && e.kind === 'auth') { this.c.fail(e); return; }
      if (++this.misses * POLL_MS >= POLL_GIVE_UP_MS) {
        st.jobError = this.L.jobLost; st.offline = false;
        if (st.banner === 'restoring') { st.banner = 'restore_failed'; st.bannerError = this.L.jobLost; }
        return;
      }
      st.offline = !(e instanceof AdminError && e.code === 'maintenance');
      this.timer = setTimeout(() => void this.poll(id), POLL_MS);
    }
  }

  private finish(job: BackupJob): void {
    const st = this.st, L = this.L;
    if (job.status === 'failed') {
      st.jobError = job.error || (job.type === 'restore' ? L.bnFailed : L.backupFailed);
      if (job.type === 'restore') { st.banner = 'restore_failed'; st.bannerError = job.error; }
      else this.c.toast(L.backupFailed);
    } else if (job.type === 'restore') st.banner = 'restored';
    else this.c.toast(L.backupDone);
    void this.reload(job.type === 'restore' ? 15 : 1);
  }

  /** Reloads the list (and after a restore the open section); maintenance may linger a moment after the job ends. */
  private async reload(attempts: number): Promise<void> {
    if (this.disposed) return;
    const ok = await this.load();
    if (ok && this.st.job?.type === 'restore') this.c.refreshData();
    if (!ok && attempts > 1 && !this.st.denied) this.timer = setTimeout(() => void this.reload(attempts - 1), POLL_MS);
  }

  dispose(): void { this.disposed = true; clearTimeout(this.timer); }
  /** (Re)mounted: resume polling a job that was active when the console unmounted. */
  attach(): void {
    if (!this.disposed) return;
    this.disposed = false;
    const j = this.st.job; if (j && jobActive(j)) this.timer = setTimeout(() => void this.poll(j.id), POLL_MS);
  }

  // ── download ──
  openDownload(b: Backup): void { this.st.dl = { id: b.id, createdAt: b.created_at, password: '', phrase: '', error: '', busy: false }; }
  async download(): Promise<void> {
    const d = this.st.dl, port = this.c.port;
    if (!d || d.busy || !d.password || !port.backupDownload) return;
    this.st.dl = { ...d, busy: true, error: '' };
    try {
      const r = await port.backupDownload(d.id, d.password);
      this.st.dl = null;
      this.c.downloadUrl(r.url);
      this.c.toast(this.L.dlStarted);
    } catch (e) {
      if (e instanceof AdminError && e.kind === 'auth') { this.st.dl = null; this.c.fail(e); return; }
      if (this.st.dl) this.st.dl = { ...this.st.dl, busy: false, error: errorText(e, this.lang) };
    }
  }

  // ── restore ──
  openRestore(b: Backup): void { if (b.status === 'ok') this.st.rs = { id: b.id, createdAt: b.created_at, expected: confirmPhrase(b.created_at, b.restore_confirmation), password: '', phrase: '', error: '', busy: false }; }
  async restore(): Promise<void> {
    const d = this.st.rs, port = this.c.port;
    if (!d || d.busy || !port.backupRestore || !restoreReady(d.password, d.phrase, expectedOf(d))) return;
    this.st.rs = { ...d, busy: true, error: '' };
    try {
      const r = await port.backupRestore(d.id, d.password, expectedOf(d));
      this.st.rs = null;
      this.track(r.job);
    } catch (e) {
      if (e instanceof AdminError && e.kind === 'auth') { this.st.rs = null; this.c.fail(e); return; }
      if (this.st.rs) this.st.rs = { ...this.st.rs, busy: false, error: errorText(e, this.lang) };
    }
  }
}

// ── view-model (same palette and value style as renderVals) ──
export function backupVals(c: AdminController, bk: BackupsCtl, now = Date.now()): Record<string, any> {
  const st = bk.st, L = bk.L, lang = bk.lang, d = st.data;
  const n = (x: number | undefined | null) => (x === null || x === undefined ? '—' : x.toLocaleString(L.numLocale));
  const when = (iso: string | null | undefined) => (iso ? dateTime(iso, lang) : '—');
  const status = d?.status, cfg = d?.config;
  const agentAt = status?.agent_seen_at ? Date.parse(status.agent_seen_at) : NaN;
  const agentDown = !!d && 'agent_seen_at' in (status || {}) && (!Number.isFinite(agentAt) || now - agentAt > AGENT_STALE_MS);
  const lastFailNewer = !!status?.last_failure_at && (!status.last_success_at || Date.parse(status.last_failure_at) > Date.parse(status.last_success_at));
  const rows = d ? [
    { label: L.lastSuccess, value: status!.last_success_at ? L.ago(status!.last_success_at, now) : L.never, sub: status!.last_success_at ? when(status!.last_success_at) : '', dot: status!.stale || !status!.last_success_at ? RED : GREEN },
    ...(lastFailNewer ? [{ label: L.lastFailure, value: L.ago(status!.last_failure_at, now), sub: when(status!.last_failure_at), dot: RED }] : []),
    { label: L.next, value: status!.next_at ? until(status!.next_at, lang, now) : '—', sub: status!.next_at ? when(status!.next_at) : '', dot: MUTED },
    ...('agent_seen_at' in status! ? [{ label: L.agent, value: agentDown ? (status!.agent_seen_at ? L.ago(status!.agent_seen_at, now) : L.agentNone) : L.agentOk(L.ago(status!.agent_seen_at, now)), sub: '', dot: agentDown ? AMBER : GREEN }] : []),
    { label: L.encryption, value: cfg!.encryption ? L.on : L.off, sub: '', dot: cfg!.encryption ? GREEN : RED },
    { label: L.offsite, value: cfg!.offsite ? L.configured : L.notConfigured, sub: '', dot: cfg!.offsite ? GREEN : AMBER },
    { label: L.keep, value: L.keepVal(n(cfg!.keep?.hourly), n(cfg!.keep?.daily), n(cfg!.keep?.monthly), n(cfg!.keep?.local)), sub: typeof cfg!.schedule === 'object' ? L.schText(cfg!.schedule.every_hours, cfg!.schedule.at) + ' · ' + L.schTz : (cfg!.schedule || ''), dot: MUTED },
  ] : [];
  const notices = d ? [
    ...(status!.stale ? [{ text: L.stale, color: RED, bg: soft(RED) }] : []),
    ...(!cfg!.encryption ? [{ text: L.encOff, color: RED, bg: soft(RED) }] : []),
    ...(agentDown ? [{ text: L.agentDown, color: AMBER, bg: soft(AMBER) }] : []),
    ...(!cfg!.offsite ? [{ text: L.offsiteOff, color: AMBER, bg: soft(AMBER) }] : []),
  ] : [];
  const stColor: Record<string, string> = { running: AMBER, ok: GREEN, failed: RED };
  const offColor: Record<string, string> = { uploaded: GREEN, pending: AMBER, failed: RED, disabled: MUTED };
  const list = (d?.backups || []).map(b => ({
    id: b.id, time: when(b.created_at), ago: L.ago(b.created_at, now), kind: L.kind[b.kind] || b.kind,
    statusLabel: L.bstatus[b.status] || b.status, stColor: stColor[b.status] || MUTED, stBg: soft(stColor[b.status] || MUTED), error: b.status === 'failed' ? b.error || '' : '',
    size: backupSize(b.size_bytes, lang),
    counts: b.counts ? L.counts(n(b.counts.users), n(b.counts.tasks), n(b.counts.files)) : '—',
    schema: b.schema === null || b.schema === undefined || b.schema === '' ? '—' : String(b.schema),
    where: [...(b.local ? [{ label: L.local, color: '#c9cacf', bg: '#34353a' }] : []),
      ...(b.offsite !== 'disabled' || !b.local ? [{ label: L.offsiteState[b.offsite] || b.offsite, color: offColor[b.offsite] || MUTED, bg: soft(offColor[b.offsite] || MUTED) }] : [])],
    canDownload: b.status === 'ok', canRestore: b.status === 'ok' && !jobActive(st.job),
    onDownload: () => bk.openDownload(b), onRestore: () => bk.openRestore(b),
  }));
  const job = st.job;
  const jColor = job ? ({ queued: MUTED, running: AMBER, done: GREEN, failed: RED } as Record<string, string>)[job.status] || MUTED : MUTED;
  const dl = st.dl, rs = st.rs;
  const rsOk = !!rs && restoreReady(rs.password, rs.phrase, expectedOf(rs));
  const input = (k: 'dl' | 'rs', f: 'password' | 'phrase') => (e: Event) => { const cur = st[k]; if (cur) st[k] = { ...cur, [f]: (e.target as HTMLInputElement).value, error: '' }; };
  const sch = st.sch, saved = typeof cfg?.schedule === 'object' ? cfg.schedule : null;
  const schVals = {
    hasSch: !!sch && !!c.port.backupSchedule, schTitle: L.schTitle, schEveryLabel: L.schEvery, schAtLabel: L.schAt, schTz: L.schTz,
    schOptions: [1, 2, 3, 4, 6, 8, 12, 24].map(v => ({ value: v, label: L.schOpt(v), selected: !!sch && sch.every === v })),
    schAt: sch?.at || '', schHint: sch ? L.schHint(sch.every, sch.at) : '', schError: sch?.error || '',
    schDirty: !!sch && !!saved && (sch.every !== saved.every_hours || sch.at !== saved.at),
    schSaveLabel: sch?.busy ? L.schSaving : L.schSave, schBusy: !!sch?.busy,
    onSchEvery: (e: Event) => bk.setEvery(Number((e.target as HTMLSelectElement).value)),
    onSchAt: (e: Event) => bk.setAt((e.target as HTMLInputElement).value),
    onSchSave: () => { void bk.saveSchedule(); },
  };
  return { ...schVals,
    L, allowed: bk.allowed(), denied: st.denied, unavailable: st.unavailable, loading: st.loading && !d, error: !d ? st.error : null,
    retry: () => void bk.load(), ready: !!d,
    backupNow: () => void bk.backupNow(), nowLabel: st.requesting ? L.starting : L.backupNow, nowOp: st.requesting || jobActive(job) || (cfg && !cfg.encryption) ? 0.5 : 1,
    nowDisabled: st.requesting || jobActive(job), rows, notices, list, empty: !!d && list.length === 0,
    hasJob: !!job, jobTitle: job ? (job.type === 'restore' ? L.jobRestore : L.jobBackup) : '', jobStatus: job ? L.jstatus[job.status] || job.status : '',
    jobColor: jColor, jobBg: soft(jColor), jobActive: jobActive(job), jobStep: job && jobActive(job) ? stepLabel(job.step, job.status, lang) : '',
    jobWhen: job ? L.ago(job.finished_at || job.started_at || job.requested_at, now) : '', jobError: st.jobError || '', jobWaiting: jobActive(job) && st.offline ? L.waiting : '',
    dismissJob: () => { if (!jobActive(st.job)) { st.job = null; st.jobError = null; } },
    // download dialog
    dlOpen: !!dl, dlPassword: dl?.password || '', dlError: dl?.error || '', dlOp: dl && dl.password && !dl.busy ? 1 : 0.5, dlWhen: dl ? when(dl.createdAt) : '',
    onDlPassword: input('dl', 'password'), closeDl: () => { st.dl = null; }, doDl: () => void bk.download(),
    // restore dialog
    rsOpen: !!rs, rsWhen: rs ? when(rs.createdAt) : '', rsPoints: rs ? L.rsPoints(when(rs.createdAt)) : [], rsPhrase: rs ? expectedOf(rs) : '',
    rsPassword: rs?.password || '', rsTyped: rs?.phrase || '', rsError: rs?.error || '', rsOk: rsOk && !rs?.busy, rsOp: rsOk && !rs?.busy ? 1 : 0.5,
    rsMismatch: !!rs && rs.phrase.trim().length > 0 && !rsOk && normalizePhrase(rs.phrase) !== expectedOf(rs) ? L.rsMismatch : '',
    onRsPassword: input('rs', 'password'), onRsPhrase: input('rs', 'phrase'), closeRs: () => { if (!st.rs?.busy) st.rs = null; }, doRs: () => void bk.restore(),
    copyPhrase: () => { if (!rs) return; try { void navigator.clipboard.writeText(expectedOf(rs)); } catch { /* clipboard unavailable */ } c.toast(L.copied); },
    // banner (rendered by AdminApp above every section)
    banner: st.banner, bannerText: st.banner === 'restoring' ? L.bnRestoring + (job && jobActive(job) && job.type === 'restore' ? ' — ' + stepLabel(job.step, job.status, lang) : '')
      : st.banner === 'restored' ? L.bnDone : st.banner === 'restore_failed' ? L.bnFailed + (st.bannerError ? ' ' + st.bannerError : '') : '',
    bannerColor: st.banner === 'restored' ? GREEN : st.banner === 'restore_failed' ? RED : AMBER,
    bannerBg: soft(st.banner === 'restored' ? GREEN : st.banner === 'restore_failed' ? RED : AMBER),
    bannerReload: st.banner === 'restored', reload: () => location.reload(),
    bannerClose: st.banner !== 'restoring' && !!st.banner, closeBanner: () => { st.banner = null; st.bannerError = null; },
  };
}
